<script lang="ts">
  import Dialog from './Dialog.svelte';
  import Icon from './Icon.svelte';
  import { photoUrl } from './api';
  import { findTodayHoursLine, isOpenNow } from './opening-hours';
  import OpenNowBadge from './OpenNowBadge.svelte';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import { googleMapsPlaceUrl } from '~/shared/routing.js';
  import type { PlacePreview } from '~/shared/api.js';

  let {
    preview,
    busy = false,
    error = null,
    hoursLocale = null,
    onConfirm,
    onClose,
  }: {
    preview: PlacePreview;
    busy?: boolean;
    error?: string | null;
    /** Language the preview's hours were fetched in — passed to the
     *  today-matcher so the locale-formatted weekday lines up with
     *  what Google returned. */
    hoursLocale?: string | null;
    onConfirm: () => void;
    onClose: () => void;
  } = $props();

  const src = $derived(photoUrl(preview.photoReference, 1080));
  const typeLabel = $derived(formatTypeLabel(preview.category));
  const detailsUrl = $derived(googleMapsPlaceUrl(preview));
  const todayHoursLine = $derived(
    preview.openingHours ? findTodayHoursLine(preview.openingHours, hoursLocale) : null,
  );
  const openStatus = $derived(
    isOpenNow(preview.openingPeriods, preview.utcOffsetMinutes),
  );
  let hoursExpanded: boolean = $state(false);

  // Swipe-to-dismiss on the drag handle (mobile bottom sheet) ─────────────
  let dragOffset = $state(0);
  let dragging = $state(false);
  let dragStartY = 0;
  let dragPointerId: number | null = null;
  const DISMISS_THRESHOLD_PX = 100;

  function onHandlePointerDown(e: PointerEvent) {
    dragging = true;
    dragStartY = e.clientY;
    dragPointerId = e.pointerId;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onHandlePointerMove(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointerId) return;
    const delta = e.clientY - dragStartY;
    dragOffset = delta > 0 ? delta : 0;
  }
  function onHandlePointerUp(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointerId) return;
    const released = dragOffset;
    dragging = false;
    dragPointerId = null;
    dragOffset = 0;
    if (e.type === 'pointerup' && released > DISMISS_THRESHOLD_PX) {
      onClose();
    }
  }
</script>

<Dialog {onClose} variant="sheet">
  <div
    class="detail"
    class:dragging
    role="dialog"
    aria-modal="true"
    aria-label={preview.name}
    style="transform: translateY({dragOffset}px);"
  >
    <button
      type="button"
      class="drag-handle"
      tabindex="-1"
      aria-label="Swipe down or tap to close"
      onpointerdown={onHandlePointerDown}
      onpointermove={onHandlePointerMove}
      onpointerup={onHandlePointerUp}
      onpointercancel={onHandlePointerUp}
      onclick={onClose}
    >
      <span class="grabber"></span>
    </button>
    <div class="photo-wrap">
      {#if src}
        <img src={src} alt="" />
      {:else}
        <div class="no-photo"></div>
      {/if}
      {#if preview.rating !== null}
        <span class="photo-badge rating-badge">
          <Icon name="star" size={11} />
          <span class="tabular-nums">{preview.rating.toFixed(1)}</span>
        </span>
      {/if}
      {#if openStatus !== null}
        <OpenNowBadge status={openStatus} />
      {/if}
    </div>
    <div class="photo-overlay">
      <button class="close-btn" type="button" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={16} />
      </button>
    </div>

    <div class="body">
      <h2 class="title">{preview.name}</h2>
      {#if typeLabel}
        <p class="meta">{typeLabel}</p>
      {/if}
      {#if preview.formattedAddress}
        <p class="address">{preview.formattedAddress}</p>
      {/if}
      {#if preview.openingHours && preview.openingHours.length > 0}
        <section class="hours" aria-label="Opening hours">
          {#if todayHoursLine && !hoursExpanded}
            <button
              type="button"
              class="hours-row-collapsed"
              aria-expanded="false"
              onclick={() => (hoursExpanded = true)}
              disabled={preview.openingHours.length <= 1}
            >
              <span class="hours-tag">Hours</span>
              <span class="hours-today">{todayHoursLine}</span>
              {#if preview.openingHours.length > 1}
                <span class="hours-more">
                  Show all
                  <Icon name="chevron-down" size={12} strokeWidth={2.25} />
                </span>
              {/if}
            </button>
          {:else}
            <div class="hours-head">
              <h3 class="hours-title">Hours</h3>
              {#if todayHoursLine}
                <button
                  type="button"
                  class="hours-toggle"
                  aria-expanded="true"
                  onclick={() => (hoursExpanded = false)}
                >
                  <Icon name="chevron-up" size={12} strokeWidth={2.25} />
                </button>
              {/if}
            </div>
            <ul class="hours-list">
              {#each preview.openingHours as line (line)}
                <li class:hours-row--today={line === todayHoursLine}>{line}</li>
              {/each}
            </ul>
          {/if}
        </section>
      {/if}
      {#if error}
        <p class="error">{error}</p>
      {/if}
    </div>

    <footer class="detail-footer">
      <button
        class="btn btn-primary nav-btn"
        type="button"
        disabled={busy || preview.alreadyBookmarked}
        onclick={onConfirm}
      >
        <span>
          {#if preview.alreadyBookmarked}
            Already in this trip
          {:else if busy}
            Bookmarking…
          {:else}
            Bookmark this place
          {/if}
        </span>
      </button>
      <a class="btn btn-secondary nav-btn nav-btn--trail" href={detailsUrl} target="_blank" rel="noopener noreferrer">
        <span>Details</span>
        <span class="nav-btn-trail" aria-hidden="true"><Icon name="arrow-up-right" size={15} strokeWidth={2} /></span>
      </a>
    </footer>
  </div>
</Dialog>

<style>
  .detail {
    position: relative;
    background: var(--color-canvas);
    width: 100%;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    box-shadow: rgba(15, 15, 15, 0.2) 0 -8px 32px -4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    touch-action: pan-y;
    transition: transform 220ms cubic-bezier(0.32, 0.72, 0, 1);
  }
  .detail.dragging { transition: none; }
  @media (min-width: 768px) {
    .detail {
      max-width: 580px;
      border-radius: var(--radius-md);
      box-shadow:
        rgba(0, 0, 0, 0.02) 0 0 0 1px,
        rgba(0, 0, 0, 0.04) 0 2px 6px 0,
        rgba(0, 0, 0, 0.10) 0 4px 8px 0;
    }
  }

  .drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    flex: none;
    background: var(--color-canvas);
    cursor: grab;
    touch-action: none;
    border: 0;
  }
  .drag-handle:active { cursor: grabbing; }
  .grabber {
    width: 36px;
    height: 4px;
    background: var(--color-border-strong);
    border-radius: 9999px;
    pointer-events: none;
  }
  @media (min-width: 768px) {
    .drag-handle { display: none; }
  }

  .photo-wrap {
    position: relative;
    aspect-ratio: 16 / 10;
    background: var(--color-surface-strong);
    overflow: hidden;
    flex: none;
  }
  @media (min-width: 768px) {
    .photo-wrap {
      aspect-ratio: auto;
      height: clamp(200px, 28vh, 280px);
    }
  }
  .photo-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .no-photo { width: 100%; height: 100%; }
  .rating-badge {
    position: absolute;
    top: calc(0.75rem + 7px);
    left: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .photo-overlay {
    position: absolute;
    top: calc(22px + 0.75rem);
    right: 0.75rem;
  }
  @media (min-width: 768px) {
    .photo-overlay { top: 0.75rem; }
  }
  .close-btn {
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 0;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.16) 0 4px 8px 0;
  }

  .body {
    padding: 1.25rem 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: var(--color-ink);
    line-height: 1.2;
    word-break: break-word;
  }
  .meta {
    margin: 0;
    font-size: 14px;
    color: var(--color-muted);
  }
  .address {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    color: var(--color-body);
  }
  .error {
    margin: 0.25rem 0 0;
    font-size: 13px;
    color: var(--color-error);
  }
  .hours {
    margin-top: 0.25rem;
  }
  .hours-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.375rem;
  }
  .hours-title {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .hours-row-collapsed {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    width: 100%;
    background: transparent;
    border: 0;
    padding: 0;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: var(--color-body);
    font-size: 13px;
    line-height: 1.4;
  }
  .hours-more {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    color: var(--color-muted);
  }
  .hours-row-collapsed:hover .hours-more { color: var(--color-ink); }
  .hours-row-collapsed:disabled { cursor: default; }
  .hours-tag {
    flex: none;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .hours-today {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hours-row-collapsed :global(svg) {
    flex: none;
    color: var(--color-muted);
  }
  .hours-row-collapsed:hover :global(svg) { color: var(--color-ink); }
  .hours-toggle {
    display: inline-flex;
    align-items: center;
    background: transparent;
    border: 0;
    padding: 0;
    color: var(--color-muted);
    cursor: pointer;
  }
  .hours-toggle:hover { color: var(--color-ink); }
  .hours-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.125rem;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-body);
  }
  .hours-row--today {
    color: var(--color-ink);
    font-weight: 600;
  }

  .detail-footer {
    flex: none;
    border-top: 1px solid var(--color-hairline-soft);
    background: var(--color-canvas);
    padding: 0.875rem 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .nav-btn {
    width: 100%;
    justify-content: center;
    min-height: 44px;
    height: 44px;
    padding: 0 1rem;
    font-size: 14px;
  }
  /* Take the trailing ↗ glyph out of flow so the text centers the same
     as the icon-less primary button stacked above. */
  .nav-btn--trail { position: relative; }
  .nav-btn-trail {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    pointer-events: none;
  }
</style>
